import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/admin/users/')({
  component: AdminUsers,
});

type UserSummary = {
  id: string;
  email: string;
  displayName: string;
  emailStatus: string;
  deletedAt: string | null;
  createdAt: string;
};

type InviteResult = { id: string; email: string; expiresAt: string; acceptUrl: string };

const INVITE_ERRORS: Record<string, string> = {
  invite_already_pending: 'An invite is already pending for that address.',
  forbidden: 'Not permitted.',
};

function InviteForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<InviteResult | null>(null);

  const mutation = useMutation({
    mutationFn: (e: string) =>
      api.post<{ invite: InviteResult }>('/v1/invites', { email: e }).then((r) => r.invite),
    onSuccess: (invite) => setSent(invite),
  });

  const inputClass =
    'flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

  if (sent) {
    return (
      <div className="rounded-lg border border-border p-4 mb-6 flex flex-col gap-2">
        <p className="text-sm font-medium">Invite sent to {sent.email}</p>
        <p className="text-xs text-muted-foreground">
          Accept URL (share if email doesn't arrive — SES sandbox requires pre-verified recipients):
        </p>
        <code className="text-xs break-all bg-muted rounded px-2 py-1">{sent.acceptUrl}</code>
        <div className="flex gap-2 mt-1">
          <Button size="sm" variant="outline" onClick={() => { setSent(null); setEmail(''); }}>
            Invite another
          </Button>
          <Button size="sm" variant="ghost" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); mutation.mutate(email); }}
      className="rounded-lg border border-border p-4 mb-6 flex flex-col gap-3"
    >
      <p className="text-sm font-medium">Invite user</p>
      <div className="flex gap-2">
        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className={inputClass}
        />
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? 'Sending…' : 'Send invite'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {mutation.isError && (
        <p className="text-sm text-destructive">
          {mutation.error instanceof ApiError
            ? (INVITE_ERRORS[mutation.error.code] ?? mutation.error.message)
            : 'Something went wrong.'}
        </p>
      )}
    </form>
  );
}

function AdminUsers() {
  const [inviting, setInviting] = useState(false);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['admin-users', q, cursor],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '25' });
      if (q) params.set('q', q);
      if (cursor) params.set('cursor', cursor);
      return api.get<{ users: UserSummary[]; nextCursor: string | null }>(
        `/v1/admin/users?${params}`,
      );
    },
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(search);
    setCursor(null);
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold">Users</h1>
        {!inviting && (
          <Button size="sm" onClick={() => setInviting(true)}>Invite user</Button>
        )}
      </div>

      {inviting && <InviteForm onDone={() => setInviting(false)} />}

      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Search by email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button type="submit">Search</Button>
        {q && (
          <Button
            variant="outline"
            type="button"
            onClick={() => { setSearch(''); setQ(''); setCursor(null); }}
          >
            Clear
          </Button>
        )}
      </form>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Joined</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data?.users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">
                    No users found.
                  </td>
                </tr>
              )}
              {data?.users.map((user) => (
                <tr key={user.id} className="hover:bg-muted/40">
                  <td className="px-4 py-2">{user.email}</td>
                  <td className="px-4 py-2">{user.displayName}</td>
                  <td className="px-4 py-2">
                    {user.deletedAt ? (
                      <span className="text-xs bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded">
                        Disabled
                      </span>
                    ) : (
                      <span className="text-xs bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to="/admin/users/$userId"
                      params={{ userId: user.id }}
                      className="text-primary hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(cursor !== null || data?.nextCursor) && (
        <div className="flex justify-between mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(null)}
            disabled={cursor === null}
          >
            First page
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(data?.nextCursor ?? null)}
            disabled={!data?.nextCursor}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}
